import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { algoApiRequest } from "../authConfig";
import { ltUrl } from "../features/lazertrader/lazerApi";
import { COMPANY } from "../MFTEnvironmentVariables";
import { acquireToken } from "./acquireToken";

/**
 * This is lazerhub's query function, and it is the *default* for every query in
 * the app: it prefixes `ltUrl()` and appends `company`. A query against any
 * other backend — the JEPX data provider, for one — must supply its own
 * `queryFn` on the `useQuery` call, or it will be misrouted to lazerhub with a
 * spurious `company` parameter.
 */
const defaultQueryFn: QueryFunction = async ({ queryKey }) => {
  const [path, params] = queryKey as [string, Record<string, string>?];

  // Every lazerhub read is scoped to this deployment's company. Done here rather
  // than per hook so a new query cannot forget it. Note the record form is
  // required: `eic` is a string[], which URLSearchParams comma-joins — which is
  // what the hub's filter expects. Repeated `eic` params would be rejected.
  const queryString = `?${new URLSearchParams({
    ...params,
    company: COMPANY,
  }).toString()}`;

  const accessToken = await acquireToken(algoApiRequest);

  const resp = await fetch(`${ltUrl()}/${path}${queryString}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!resp.ok) {
    throw new Error(resp.statusText);
  }

  try {
    const data = await resp.json();

    return data;
  } catch {
    return null;
  }
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
      retry: 2,
      staleTime: 2000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});
